import { type WorklistColumnViewParam, WorklistSortingParam } from '@/Models/GenericWorklist/WorklistParams';
import { type OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import ContextMenuWrapper from '@/Pages/GenericWorklist/Components/ContextMenu/ContextMenuWrapper';
import { WorklistDataGridRow } from '@/Pages/GenericWorklist/Components/DataGrid/WorklistDataGridRow';
import WorklistDataGridStore from '@/Pages/GenericWorklist/Components/DataGrid/WorklistDataGridStore';
import RemarksPopup from '@/Pages/GenericWorklist/Components/RemarksPopup';
import JustificationPopup from '@/Pages/GenericWorklist/Components/JustificationPopup/JustificationPopup';
import { ReportPopup } from '@/Pages/GenericWorklist/Components/ReportPopup';
import {
    DobContextMenuRoot,
    DobContextMenuTrigger,
    DobLoadingIndicator,
    DobTable,
    DobTableDataCell,
    DobTableRow, SortState,
} from '@dedalusdiit/dob-components';
import { getCoreRowModel, useReactTable, type Row, type VisibilityState } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type WorklistSortStore from '../../Stores/WorklistSortStore';
import type IWorklistStore from '../../Stores/WorklistStore';
import { NoResults } from '../NoResults';
import WorklistDataGridHeader from './Header/WorklistDataGridHeader';
import Styles from './WorklistDataGrid.module.scss';
import { getWorklistDataGridColumnSetup } from './WorklistDataGridConfig';
import WorklistDataGridHandler from './WorklistDataGridHandler';
import ReportDialog from '@/Pages/GenericWorklist/Components/Dialogs/ReportDialog/ReportDialog';
import { type IWorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';

function mapColumnsOrder(colData: WorklistColumnViewParam[]): string[] {
    return colData.map((x) => x.ID);
}

function mapVisibilityState(columnConfig: WorklistColumnViewParam[]): VisibilityState {
    return columnConfig.reduce<VisibilityState>(
        (visibilityState, column) => ({
            ...visibilityState,
            [column.ID]: column.Visible,
        }), {});
}

export interface WorklistDataGridProps {
    worklistStore: IWorklistStore;
    worklistSortStore: WorklistSortStore;
    worklistSelectionHelper: IWorklistSelectionHelper;
    worklistDataGridHandler: WorklistDataGridHandler;
    dataGridStore: WorklistDataGridStore;
}

const WorklistDataGrid = observer((props: WorklistDataGridProps): React.ReactElement => {
    const { worklistStore, worklistSortStore, worklistSelectionHelper, worklistDataGridHandler, dataGridStore } = props;
    const { columnConfig } = worklistDataGridHandler;

    const data = worklistDataGridHandler.ordersToDisplay;
    const columnSetup = useMemo(
        () => getWorklistDataGridColumnSetup(worklistSelectionHelper, dataGridStore),
        [worklistSelectionHelper, dataGridStore],
    );
    const columnOrder = useMemo(() => mapColumnsOrder(columnConfig), [columnConfig]);
    const columnVisibility = useMemo(() => mapVisibilityState(columnConfig), [columnConfig]);

    const table = useReactTable({
        columns: columnSetup,
        data,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: {
            columnOrder,
            columnVisibility,
        },
    });

    const rows = [...table.getRowModel().rows]; // Needed since static reference to `.rows` doesnt trigger re-render in child component if eg. visiblity changes
    const columnHeaders = [...table.getHeaderGroups()[0].headers];
    const visibleColumnsKey = table.getVisibleLeafColumns().map((column) => column.id).join('|');
    const {
        showJustificationModalIsOpen,
        selectedOrderForJustification,
        setJustificationModalNotVisible,
        selectedOrderForReportPopup,
        selectedReportPopupProcedureIds,
        selectedReportPopupReportId,
        showReportModalIsOpen,
        setReportModalNotVisible,
    } = dataGridStore;

    const onSortingChanged = (sortState: SortState[]): void => {
        if (worklistSortStore.isSortingDisabled) {
            return;
        }

        const sortingParams = sortState.map((x) => ({
            Priority: x.order,
            Column: x.columnKey,
            Desc: x.direction == 'desc',
        } as WorklistSortingParam));

        worklistStore.applySorting(sortingParams);
    };

    const handleContextMenuOpenChange = (open: boolean): void => {
        if (open) {
            return;
        }

        worklistStore.setSelection([]);
    };

    return (
        <DobContextMenuRoot
            onOpenChange={handleContextMenuOpenChange}
        >
            <DobTable
                sorting={worklistSortStore.currentSortState}
                onSortingChanged={onSortingChanged}
                clickableRows={true}
                color="primary"
            >
                <WorklistDataGridHeader
                    headers={columnHeaders}
                    worklistSortStore={worklistSortStore}
                    selectionStore={worklistSelectionHelper}
                />
                <DobContextMenuTrigger asChild>
                    <tbody>
                        {worklistDataGridHandler.isLoadingData && (
                            <DobTableRow>
                                <DobTableDataCell colSpan={columnHeaders.length} className={Styles.loading}><DobLoadingIndicator /></DobTableDataCell>
                            </DobTableRow>
                        )}

                        <WorklistDataGridResults
                            isLoadingData={worklistDataGridHandler.isLoadingData}
                            hasResults={worklistDataGridHandler.hasResults}
                            filterTooBroad={worklistDataGridHandler.filterTooBroad}
                            rows={rows}
                            worklistStore={worklistStore}
                            worklistSelectionHelper={worklistSelectionHelper}
                            visibleColumnsKey={visibleColumnsKey}
                        />
                    </tbody>
                </DobContextMenuTrigger>
            </DobTable>

            <ContextMenuWrapper
                worklistStore={worklistStore}
                worklistSelectionHelper={worklistSelectionHelper}
            />
            {showJustificationModalIsOpen && selectedOrderForJustification && <JustificationPopup isOpen={showJustificationModalIsOpen} selectedOrder={selectedOrderForJustification} setNotVisible={setJustificationModalNotVisible} refreshWorklist={worklistStore.refetchResults} />}
            <RemarksPopupWrapper dataGridStore={dataGridStore} />
            <ReportDialog
                worklistStore={worklistStore}
                onClose={() => worklistStore.setReportDialogOpen(false)}
            />
            {showReportModalIsOpen && selectedOrderForReportPopup && (
                <ReportPopup
                    isOpen={showReportModalIsOpen}
                    onClose={setReportModalNotVisible}
                    selectedOrder={selectedOrderForReportPopup}
                    selectedReportId={selectedReportPopupReportId}
                    selectedReportProcedureIds={selectedReportPopupProcedureIds ?? []}
                />
            )}
        </DobContextMenuRoot>
    );
});

// Created this as a separate component so we don't trigger unnecessary rerenders in sibling components
const RemarksPopupWrapper = observer((props: Readonly<{ dataGridStore: WorklistDataGridStore }>) => {
    return <>{!props.dataGridStore.isLoadingRemarks && <RemarksPopup dataGridStore={props.dataGridStore} />}</>;
});

const WorklistDataGridResults = observer((props: Readonly<{ rows: Row<OrderForWorklist>[]; isLoadingData: boolean; hasResults: boolean; filterTooBroad: boolean; worklistStore: IWorklistStore; worklistSelectionHelper: IWorklistSelectionHelper; visibleColumnsKey: string }>): ReactElement => {
    const { hasResults, rows, worklistStore, worklistSelectionHelper, isLoadingData, filterTooBroad, visibleColumnsKey } = props;
    const navigate = useNavigate();

    if (isLoadingData) {
        return <></>;
    }

    if (!hasResults) {
        return <NoResults filterTooBroad={filterTooBroad} />;
    }

    return (
        <>
            {rows.map((row) => (
                <WorklistDataGridRow key={`${row.id}|${visibleColumnsKey}`} row={row} navigateFunction={navigate} worklistStore={worklistStore} worklistSelectionHelper={worklistSelectionHelper} />
            ))}
        </>
    );
});

export default WorklistDataGrid;
